import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function Pathways() {
  const [pathways, setPathways] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get("/pathways").then((r) => setPathways(r.data)).catch(() => {});
  }, []);

  const current = pathways[active];

  return (
    <div className="bg-navy-base min-h-screen" data-testid="pathways-page">
      <SEO title="Career Pathways — AI, Data, Cybersecurity, Web, Accounting | XTM TECHNOLOGIES Jorhat"
        description="Visual career roadmaps: Class 12 to AI careers, data analyst, cybersecurity, web development, cloud, accounting, electronics, beauty & wellness and entrepreneurship." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Career Pathways</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-4" data-testid="pathways-heading">
            Your Roadmap from Classroom to Career
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">Every pathway uses real XTM programs. Select a goal to see the step-by-step route.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex flex-wrap gap-2 mb-12" data-testid="pathway-selector">
          {pathways.map((p, i) => (
            <button key={p.id} onClick={() => setActive(i)}
              className={`text-xs sm:text-sm font-semibold rounded-full px-4 py-2 transition-colors border ${i === active ? "bg-orange-600 border-orange-600 text-white" : "border-white/15 text-slate-300 hover:border-orange-500/40 hover:text-orange-300"}`}
              data-testid={`pathway-tab-${p.title.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              {p.title}
            </button>
          ))}
        </div>

        {current && (
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-display text-2xl font-bold text-white mb-2" data-testid="active-pathway-title">{current.title}</h2>
              <p className="text-sm text-orange-400 mb-8">{current.goal}</p>
              <div className="space-y-0" data-testid="pathway-steps">
                {current.steps.map((s, i) => (
                  <div key={i} className="flex flex-col items-start">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0 ${i === current.steps.length - 1 ? "bg-orange-600 text-white" : "bg-navy-surface border border-white/15 text-slate-300"}`}>
                        {i + 1}
                      </div>
                      <div className={`font-medium ${i === current.steps.length - 1 ? "text-orange-400 font-semibold" : "text-white"}`}>{s}</div>
                    </div>
                    {i < current.steps.length - 1 && (
                      <div className="ml-5 py-1 text-orange-500"><ArrowDown className="w-4 h-4" /></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-white/10 rounded-2xl p-8 lg:sticky lg:top-28">
              <h3 className="font-display text-lg font-semibold text-white mb-3">Start this pathway</h3>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                This roadmap is guidance, not a guarantee of employment. A counsellor can adapt it to your education, budget and timeline using only currently available XTM programs.
              </p>
              <div className="space-y-3">
                <Button asChild className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition-colors" data-testid="pathway-apply-btn">
                  <Link to="/admissions">Apply for Admission</Link>
                </Button>
                <Button asChild variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-xl transition-colors" data-testid="pathway-ai-btn">
                  <Link to="/career-ai">Ask XTM Career AI</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
